import { Switch,Match, createMemo } from 'solid-js';
import { VisualWithInfo } from 'Shared/frontend/widgets/VisualWithInfo';
import { ImageBlock } from 'Shared/frontend/widgets/ImageBlock';
import { Styles } from 'Shared/frontend/widgets/Styles';
import { Overlay } from 'Shared/frontend/widgets/Overlay';
import { Slides } from 'Shared/frontend/widgets/Slides';
import { Popup } from 'Shared/frontend/widgets/Popup';
import { Embed } from 'Shared/frontend/widgets/Embed';
import { Buttons } from 'Shared/frontend/widgets/Buttons';
import { Content } from 'Shared/frontend/widgets/Content';
import { Vertical } from 'Shared/frontend/widgets/Vertical';
import { TextWidget } from 'Shared/frontend/widgets/TextWidget';
import { Link } from 'Shared/frontend/widgets/Link';
import { Grid } from 'Shared/frontend/widgets/Grid';
import { IgnorePageMarginsStyle } from 'Shared/frontend/IgnorePageMarginsStyle';
import { Parallax } from 'Shared/frontend/widgets/Parallax';
import { StylesProvider, useStylePath, useStyleDetails, useChildKey } from 'Shared/frontend/widgets/StylesProvider';
import { applicableStyles } from 'Shared/frontend/applicableStyles';
import { AnimationWrap } from 'Shared/frontend/widgets/AnimationWrap';
import type { VisualWithInfoLayout, StylesLayout, EmbedLayout, ImageLayout, OverlayLayout, PopupLayout, PostersLayout, Layout, GridLayout, VerticalLayout, TextLayout, LinkLayout } from 'Shared/model/Components';


/*
	NOTE regarding heights:

	Single entry Grids(?) can be used to resolve height conflicts between parents and children if necessary. 
	These can be thought of as being "overflows" and "underflows".
	In both cases we may wish to add a vertical alignment option or theme option.
	Single entry Grids allows for letterboxing.
	If we want to allow for letterboxing on the sides we may wish in time to add a width option.

	NOTE: it would be harder to vertically center items in Grids or sliders - currently we stretch
	      out the cells vertically so child items can inherit height. Perhaps we wouldn't have to do this though
		  if 'fit content' were being used.
		  If we do add overflow to them, it should be to their elements really, not to the whole component. 
		  Note that adding cropping and alignment to these Components would make the layout harder to debug
		  so they may be better left as is.
*/

/*
	NOTE regarding wide components and sections. 

	The best approach appears to be to use negative margins.
	Note margins don't collapse horizontally, which limits our options somewhat.

    Note that Visual with Info, Vertical Layout, etc may wish to (optionally) allow images to be flush to 
	the sides on mobile. In this case the component itself needs to add margins to the info. This means 
	that only allowing the sections and/or the Style components to modify the side margins is really enough: 
	the particular components need this power (although they could be triggered by settings in Style potentially).

	If we need full control over the side margins then we need to create an algorithm that calculates 
	remaining side margins and makes it available to components. This would allow eg a component to claim half the
	page margin and a child to claim the rest.

	A less powerful, and easier solution is just to allow components to apply a negative page side margin.
*/


export function Component(P:Layout)
{
	const styleDetails = useStyleDetails();
	const stylePath = useStylePath();
	const childKey = useChildKey();

	const classes = () => {
		/* Text component types are be treated as classes, at least for the moment... */
		if (P.layout == 'text')
			return [...P.classes ?? [],P.type];
		/* ...and so are the bare elements a buttons or link widget renders - see elementTypes. */
		if (P.layout == 'buttons')
			return [...P.classes ?? [],'button'];
		if (P.layout == 'link')
			return [...P.classes ?? [],'a'];
		return P.classes;
	};

	const newStylePath = () => [...stylePath,{key:P.layout,childKey:childKey(),classes:classes()}];
	const applicStyles = createMemo(() => applicableStyles(newStylePath(),styleDetails()));

	const ignorePageMargins = () => applicStyles().ignorePageMargins;
	const useParallax = () => applicStyles().parallax;
	const scrollRate = () => 0.3;  //XXX the townie has been using 0.3

	return (
		<StylesProvider path={newStylePath()} styleDetails={styleDetails}> 
			<IgnorePageMarginsStyle disable={! ignorePageMargins()}>
				<AnimationWrap animations={applicStyles().animations}>
					<Switch>
						<Match when={useParallax()}>
							<Parallax scrollRate={scrollRate()}>
								<ComponentInner {...P} styling={applicStyles()} />
							</Parallax>
						</Match>
						<Match when={true}>
							<ComponentInner {...P} styling={applicStyles()} />
						</Match>
					</Switch>
				</AnimationWrap>
			</IgnorePageMarginsStyle>
		</StylesProvider>
	);
}


function ComponentInner(P:Layout & {styling,height})
{
//export function Styles(P: {styles:string[],removeStyles:string[],classes:string[]})
	return (
		<Switch>
			<Match when={P.layout == 'styles'}>
				<Styles {...P as StylesLayout} />
			</Match>
			<Match when={P.layout == 'link'}>
				<Link {...P as LinkLayout} />
			</Match>
			<Match when={P.layout == 'grid'}>
				<Grid {...P as GridLayout} />
			</Match>
			<Match when={P.layout == 'vertical'}>
				<Vertical {...P as VerticalLayout} />
			</Match>
			<Match when={P.layout == 'text'}>
				<TextWidget {...P as TextLayout} />
			</Match>
			<Match when={P.layout == 'slides'}>
				<Slides slides={P.slides} height={P.height} styling={P.styling} />
			</Match>
			<Match when={P.layout == 'visualWithInfo'}>
				<VisualWithInfo {...P as VisualWithInfoLayout} /> 
			</Match>
			<Match when={P.layout == 'image'}>
				<ImageBlock {...P as ImageLayout} /> 
			</Match>
			<Match when={P.layout == 'overlay'}>
				<Overlay {...P as OverlayLayout} />
			</Match>
			<Match when={P.layout == 'posters'}>
				<Content {...P as PostersLayout} />
			</Match>
			<Match when={P.layout == 'buttons'}>
				<Buttons {...P } />
			</Match>
			<Match when={P.layout == 'popup'}>
				<Popup {...P as PopupLayout} />
			</Match>
			<Match when={P.layout == 'embed'}>
				<Embed {...P as EmbedLayout} />
			</Match>
		</Switch>
	);
}

